interface RegulationHeading {
  title: string;
  id: string;
}

export interface RegulationSubtopic extends RegulationHeading {
  /** Nível do título de origem: 3 para `h3`, 4 para `h4`… */
  level: number;
}

export interface RegulationTopic extends RegulationHeading {
  subtitles: RegulationSubtopic[];
}

export interface RegulationTopics {
  /** O HTML de entrada com o `id` de cada título preenchido. */
  html: string;
  titles: RegulationTopic[];
}

/** "6.2.1 Conceito" → "conceito"; "Tabela de Pontos" → "tabelaDePontos". */
function slugify(text: string) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s]/gi, '')
    .replace(/\d+/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word, index) => (index === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join('');
}

/**
 * Monta a estrutura de tópicos do regulamento: `h1`/`h2` são os tópicos, `h3`–`h6` até o
 * próximo tópico são os subtópicos. Cada título recebe um `id` para servir de âncora.
 *
 * Títulos repetidos ganham sufixo (`-2`, `-3`…): com o mesmo `id`, o link de todos levava
 * ao primeiro. A primeira ocorrência mantém o `id` de antes, e os links já existentes seguem valendo.
 */
export function buildRegulationTopics(html: string): RegulationTopics {
  const container = document.createElement('div');
  container.innerHTML = html;

  const usedIds = new Map<string, number>();
  const uniqueId = (text: string) => {
    const base = slugify(text);
    const count = (usedIds.get(base) ?? 0) + 1;
    usedIds.set(base, count);

    return count === 1 ? base : `${base}-${count}`;
  };

  const titleElements = container.querySelectorAll('h1, h2');
  const titles: RegulationTopic[] = [];

  titleElements.forEach((titleElement, index) => {
    const title = titleElement.textContent;
    if (!title) return;

    const id = uniqueId(title);
    titleElement.setAttribute('id', id);

    const subtitles: RegulationSubtopic[] = [];
    const nextTitleElement = titleElements[index + 1] || container.lastElementChild;
    let sibling = titleElement.nextElementSibling;

    while (sibling && sibling !== nextTitleElement) {
      const subtitle = sibling.textContent;

      if (/^H[3-6]$/.test(sibling.tagName) && subtitle) {
        const subtitleId = uniqueId(subtitle);
        sibling.setAttribute('id', subtitleId);
        subtitles.push({ title: subtitle, id: subtitleId, level: Number(sibling.tagName[1]) });
      }

      sibling = sibling.nextElementSibling;
    }

    titles.push({ title, id, subtitles });
  });

  return { html: container.innerHTML, titles };
}
